import { SummarySection } from "@/screens/loans/components/SummarySection"
import {
  buildBorrowerDetailsItems,
  buildClosedLoanInfoItems,
  buildClosedLoanLinks,
  buildContractDetailItems,
  buildLoanDetailsItems,
  buildPropertyInfoItems,
} from "@/screens/loans/utils/LoanUtils"
import { userQueryOptions } from "@/queries/user"
import { useQuery } from "@tanstack/react-query"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import React, { useCallback } from "react"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { cn } from "@/components/util/cn"
import { Body, H3, H4, H5, H6, Small } from "@/components/standard/BFText"
import { BFLink } from "@/components/buttons/Links"
import { numberWithCommas } from "@/utils/NumberUtils"
import { usePostCloseFeature } from "@/hooks/features/usePostCloseFeature"
import { Pressable, View } from "react-native"
import ToolLine from "@/components/util/ToolLine"
import Rocket from "@/assets/clipart/rocket.svg"
import Bell from "@/assets/clipart/techny-notification-icon.svg"
import Spacer from "@/components/util/Spacer"
import { BFColors } from "@/constants/Colors"
import Money from "phosphor-react-native/src/icons/Money"
import Headphones from "phosphor-react-native/src/icons/Headset"
import Servicer from "phosphor-react-native/src/icons/CallBell"
import Hammer from "phosphor-react-native/src/icons/Hammer"
import LoanTeamCard from "@/screens/loans/components/dashboard/LoanTeamCard"
import { BFButton } from "@/components/buttons/CommonButtons"
import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"
import { useSetAnalysisNotification } from "@/queries/analysis"
import {
  type AllProductTypes,
  isZeroGravity,
} from "@/screens/loans/utils/LoanCapitalProducts"

export function BorrowerDetailSection({ loanId }: { loanId: string }) {
  const { data: userProfile } = useQuery({
    ...userQueryOptions,
    select: (data) => {
      return data.me!
    },
  })

  const queryOptions = loanDashboardQueryOptions(loanId)

  const borrowerDetailsItemsQuery = useQuery({
    ...queryOptions,
    select: useCallback(
      (data: Parameters<NonNullable<(typeof queryOptions)["select"]>>[0]) => {
        if (!data || !userProfile) {
          return []
        }

        return buildBorrowerDetailsItems(userProfile, data)
      },
      [userProfile]
    ),
  })

  if (!borrowerDetailsItemsQuery.data) {
    return null
  }

  return (
    <SummarySection
      title="Borrower Details"
      items={borrowerDetailsItemsQuery.data}
    />
  )
}

export function ContractDetailSection({ loanId }: { loanId: string }) {
  const contractDetailsItemsQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      if (!data) {
        return []
      }

      return buildContractDetailItems(data)
    },
  })

  if (!contractDetailsItemsQuery.data) {
    return null
  }

  return (
    <SummarySection
      title="Contract Details"
      items={contractDetailsItemsQuery.data}
    />
  )
}

export function PropertyInfoSection({ loanId }: { loanId: string }) {
  const propertyInfoItemsQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      if (!data) {
        return []
      }

      return buildPropertyInfoItems(data)
    },
  })

  if (!propertyInfoItemsQuery.data) {
    return null
  }

  return (
    <SummarySection title="Property Info" items={propertyInfoItemsQuery.data} />
  )
}

export function LoanDetailsSection({ loanId }: { loanId: string }) {
  const loanDetailsItemsQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      if (!data) {
        return []
      }

      return buildLoanDetailsItems(data)
    },
  })

  if (!loanDetailsItemsQuery.data) {
    return null
  }

  return (
    <SummarySection title="Loan Details" items={loanDetailsItemsQuery.data} />
  )
}

export function MonthlyPaymentsSection({ loanId }: { loanId: string }) {
  // Select the monthlyPayments array from the postClose data and loanProduct
  const {
    monthlyPayments,
    fciPaymentUrl,
    backflipPaymentUrl,
    loanProduct,
    isLoading,
    postCloseFeatureEnabled,
  } = usePostCloseFeature(loanId)

  if (
    !postCloseFeatureEnabled ||
    isLoading ||
    !monthlyPayments ||
    monthlyPayments.length === 0
  ) {
    return null
  }

  // If we have two payments, show separate A/B cards
  const [paymentA, paymentB] = monthlyPayments

  if (isZeroGravity(loanProduct as AllProductTypes)) {
    return (
      <Vertical>
        <H4 className={"mb-2"}>Monthly Payments</H4>
        <Vertical
          className={cn(
            "p-5 bg-white border rounded-md border-light-grey mb-4"
          )}
        >
          <Horizontal>
            <Vertical className={"ml-1"}>
              <H3 className={"text-midnight-blue"}>Zero Gravity Loan</H3>
              <Body className={"text-midnight-blue"}>
                {`Woohoo! No interest\npayments for 6 months!`}
              </Body>
            </Vertical>
            <Spacer />
            <Rocket style={{ marginRight: 8 }} />
          </Horizontal>
        </Vertical>
      </Vertical>
    )
  }

  return (
    <Vertical>
      <H4 className={"mb-2"}>Monthly Payments</H4>
      <Vertical
        className={cn("p-5 bg-white border rounded-md border-light-grey mb-4")}
      >
        {paymentA && (
          <SinglePaymentBlock
            testId="monthly-payments-card-A"
            loanNumber={paymentA.loanNumber ?? undefined}
            payment={{ monthlyPayment: paymentA.monthlyPayment ?? undefined }}
            linkUrl={paymentA.servicerLink || fciPaymentUrl || ""}
            linkText={paymentA.servicer || "Payment"}
          />
        )}
        {paymentB && (
          <>
            <ToolLine className={"my-4"} />
            <SinglePaymentBlock
              testId="monthly-payments-card-B"
              loanNumber={paymentB.loanNumber ?? undefined}
              payment={{ monthlyPayment: paymentB.monthlyPayment ?? undefined }}
              linkUrl={paymentB.servicerLink || backflipPaymentUrl || ""}
              linkText={paymentB.servicer || "Backflip Payment"}
            />
          </>
        )}
      </Vertical>
    </Vertical>
  )
}

function SinglePaymentBlock({
  testId,
  loanNumber,
  payment,
  linkUrl,
  linkText,
}: {
  testId: string
  loanNumber?: string
  payment: { monthlyPayment?: number }
  linkUrl: string
  linkText: string
}) {
  return (
    <Pressable testID={testId}>
      {({ pressed }) => (
        <>
          {loanNumber && <H5 className="mb-3">Loan #{loanNumber}</H5>}

          {/* Monthly Payment Row */}
          <LoanIconRow icon={Money}>
            <Horizontal className="justify-between flex-1">
              <Small className="text-midnight-blue">
                Monthly interest payment
              </Small>
              <H5 className="justify-end">
                ${numberWithCommas(payment?.monthlyPayment) ?? "n/a"}
              </H5>
            </Horizontal>
          </LoanIconRow>

          {/* Servicer Row / Payment Link */}
          <LoanIconRow icon={Servicer}>
            <Horizontal className="justify-between flex-1">
              <Small className="text-midnight-blue">Servicer</Small>
              <BFLink
                size="sm"
                showsIcon={true}
                testId={`${testId}-link`}
                url={linkUrl}
                text={linkText}
              />
            </Horizontal>
          </LoanIconRow>
        </>
      )}
    </Pressable>
  )
}

const LoanIconRow = ({
  icon: Icon,
  children,
}: {
  icon: React.ElementType
  children: React.ReactNode
}) => {
  return (
    <Horizontal className="gap-2 h-6 items-center w-full mb-2">
      <Icon size={24} />
      {children}
    </Horizontal>
  )
}

interface LoanDetailPressableCardProps {
  children: React.ReactNode
  onPress?: () => void
  testID: string
}

export function LoanDetailPressableCard({
  children,
  onPress = () => {},
  testID,
}: LoanDetailPressableCardProps) {
  return (
    <Vertical>
      <Pressable testID={testID} onPress={onPress}>
        {({ pressed }) => (
          <Vertical
            className={cn(
              "p-5 bg-white border rounded-md border-light-grey mb-4",
              pressed && "opacity-50"
            )}
          >
            {children}
          </Vertical>
        )}
      </Pressable>
    </Vertical>
  )
}

export function YourTeamSection({ loanId }: { loanId: string }) {
  const { team, isLoading, postCloseFeatureEnabled } =
    usePostCloseFeature(loanId)

  if (!postCloseFeatureEnabled || isLoading) {
    return null
  }

  const memberRepresentative = team?.memberRepresentative
  const servicingAnalyst = team?.servicingAnalyst
  const drawsAnalyst = team?.drawsAnalyst
  
  // Don't render the section if no team members are present
  if (!memberRepresentative && !servicingAnalyst && !drawsAnalyst) {
    return null
  }

  return (
    <Vertical className="w-full">
      <H4 className="mb-2">Your Team</H4>
      {memberRepresentative && (
        <LoanTeamCard
          name={memberRepresentative?.name || ""}
          role="Member Representative"
          phoneNumber={memberRepresentative?.phone || ""}
          emailAddress={memberRepresentative?.email || ""}
          circleIcon={<Headphones />}
          circleColor={BFColors.beige}
        />
      )}

      {servicingAnalyst && (
        <LoanTeamCard
          name={servicingAnalyst.name || ""}
          role="Servicing Associate"
          phoneNumber={servicingAnalyst.phone || undefined}
          emailAddress={servicingAnalyst?.email || undefined}
          circleIcon={<Servicer />}
          circleColor={BFColors.neonYellow}
        />
      )}
      {drawsAnalyst && (
        <LoanTeamCard
          name={drawsAnalyst.name || ""}
          role="Draw Analyst"
          phoneNumber={drawsAnalyst?.phone || undefined}
          emailAddress={drawsAnalyst?.email || undefined}
          circleIcon={<Hammer />}
          circleColor={BFColors.teal}
        />
      )}
    </Vertical>
  )
}

export function ClosedLoanInfoSection({ loanId }: { loanId: string }) {
  const { isLoading, postCloseFeatureEnabled } = usePostCloseFeature(loanId)
  const loanDetailsQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      if (!data) return { items: [], links: [] }
      // Build info items and links using helper functions
      const items = buildClosedLoanInfoItems(data) ?? []
      const links = buildClosedLoanLinks(data)
      return { items, links }
    },
  })

  if (!postCloseFeatureEnabled || !loanDetailsQuery.data || isLoading) {
    return null
  }

  const { items, links } = loanDetailsQuery.data

  return (
    <Vertical className="w-full grow">
      <SummarySection
        title="Closed Loan Info"
        links={links}
        toolLineIndex={2}
        items={items}
      />
    </Vertical>
  )
}

export function CompNotificationsSection({ loanId }: { loanId: string }) {
  const {
    isLoading,
    postCloseFeatureEnabled,
    notificationsEnabled,
    refetch,
    analysisId,
  } = usePostCloseFeature(loanId)

  const analysisNotificationMutation = useSetAnalysisNotification()

  if (!postCloseFeatureEnabled || isLoading) {
    return null
  }

  // Hide the banner if notifications are already enabled
  if (notificationsEnabled) {
    return null
  }

  const onGetAlerts = () => {
    if (!analysisId) {
      showErrorToast({
        title: "Error",
        message: "Analysis not found",
      })
      return
    }
    analysisNotificationMutation.mutate(
      { analysisId, enableNotifications: true },
      {
        onSuccess: () => {
          // Refetch to update notificationsEnabled and show a success toast
          void refetch().then(() => {
            showSuccessToast({
              title: "Great!",
              message:
                "We’ll send you an email when there are changes to the comps for this property.",
              autoHide: true,
            })
          })
        },
        onError: (err) => {
          console.error("Enable Notification Error", err)
          showErrorToast({
            title: "Error",
            message: JSON.stringify(err),
          })
        },
      }
    )
  }

  return (
    <Vertical className="flex-1 w-full">
      <View className="flex flex-col bg-grey border border-light-grey rounded p-3 py-2">
        <Horizontal className={"self-center"}>
          <Bell width={28} height={28} style={{ marginTop: 8 }} />
          <Vertical className="flex-1 min-w-[185px] ml-3">
            <H6 className="text-midnight-blue ">
              Stay on Top of Market Changes!
            </H6>
            <Small className="text-midnight-blue">
              Get notified when comps are sold around your property
            </Small>
          </Vertical>

          <BFButton
            className={"max-w-[77px] max-h-[27px] self-center"}
            title={"Get Alert"}
            testID={"get-alert-button"}
            size={"sm"}
            onPress={onGetAlerts}
          />
        </Horizontal>
      </View>
    </Vertical>
  )
}
